import * as React from "react";
import { connect } from "react-redux";
import { RootState } from "../../store";
import { withTranslation } from "react-i18next";
import { PromotionCodeScope } from "../../model/PromotionCodeScope";
import * as adminUserSelectors from "./adminUserSelectors";
import * as adminUserActions from "./adminUserActions";
import * as authSelectors from "../auth/authSelectors";
import { OrganizationKeyName } from "../../model/Organization";
import { AdminUser } from "../../service/api-response/AdminUserSuccessResponse";
import { OrganizationListItem } from "../organization/model/OrganizationListSuccessResponse";
import { Roles } from "../../model/RolesEnum";
import * as _ from "lodash";
import { Dispatch } from "redux";

interface OwnProps {
  adminUser: undefined | AdminUser;
  organization: OrganizationKeyName;
  organizations: OrganizationListItem[];
}

interface Props extends OwnProps {
  errors: { [field: string]: string } | null;
  canManageAdminOrganizationAccess: boolean;
  isAdminOrgSelected: boolean;
  isSuperAdmin: boolean;
  modifyRole: (
    org: OrganizationKeyName,
    userId: number,
    role: string,
    add: boolean
  ) => void;
  modifyOrgAccess: (
    org: OrganizationKeyName,
    orgId: string,
    userId: number,
    add: boolean
  ) => void;
}

interface State {
  isAutocompleteUserPopupVisible: boolean;
  orgAccess: { [orgKey: string]: boolean };
  roles: { [roleKey: string]: boolean };
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
  const organization = authSelectors.selectedOrganizationKeyName(state);
  return {
    organization,
    errors: adminUserSelectors.upsertErrors(state),
    isUpserting: adminUserSelectors.isUpserting(state),
    canManageAdminOrganizationAccess: authSelectors.canManageAdminOrganizationAccess(
      state
    ),
    isAdminOrgSelected: authSelectors.isSelectedOrganizationAdmins(state),
    isSuperAdmin: authSelectors.isSuperAdmin(state),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    modifyRole: (
      org: OrganizationKeyName,
      userId: number,
      role: string,
      add: boolean
    ) => dispatch(adminUserActions.modifyRoleAction(org, userId, role, add)),
    modifyOrgAccess: (
      org: OrganizationKeyName,
      orgId: string,
      userId: number,
      add: boolean
    ) =>
      dispatch(adminUserActions.modifyOrgAccessAction(org, orgId, userId, add)),
  };
};

class AdminUserUpsertForm extends React.Component<Props, State> {
  public state = {
    isAutocompleteUserPopupVisible: false,
    orgAccess: {},
    roles: {},
  };

  isEdit(): boolean {
    return !!this.props.adminUser;
  }

  getMustBeTrialPeriodEligibleValueBasedOnScope(
    scope: PromotionCodeScope
  ): boolean {
    return scope === PromotionCodeScope.Subscription;
  }

  componentDidMount() {
    if (this.props.adminUser) {
      const roles = _.reduce(
        this.props.adminUser.roles,
        (acc, el) => {
          return {
            ...acc,
            [el]: true,
          };
        },
        {}
      );

      const orgAccess = _.reduce(
        this.props.adminUser.organizationAccess,
        (acc, el) => {
          return {
            ...acc,
            [el]: true,
          };
        },
        {}
      );

      this.setState({
        orgAccess,
        roles,
      });
    }
  }

  getUserId(): number {
    return (this.props.adminUser || ({} as any)).id as any;
  }

  modifyRole = (role: string, add: boolean) => {
    this.setState({
      roles: {
        ...this.state.roles,
        [role]: add,
      },
    });
    this.props.modifyRole(this.props.organization, this.getUserId(), role, add);
  };

  modifyOrgAccess = (orgId: string, add: boolean) => {
    this.setState({
      orgAccess: {
        ...this.state.orgAccess,
        [orgId]: add,
      },
    });
    this.props.modifyOrgAccess(
      this.props.organization,
      orgId,
      this.getUserId(),
      add
    );
  };

  render() {
    const adminUser = this.props.adminUser;
    if (this.isEdit() && !adminUser) {
      return null;
    }

    // let defaultValues: any = {};

    // if (this.isEdit()) {
    //   defaultValues = {
    //     ...defaultValues,
    //     ...adminUser,
    //   };
    // }

    const username = (adminUser || ({} as any)).username;

    return (
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {this.isEdit() ? `Edit ${username}` : "Add admin user"}
          </h3>
        </div>
        <div className="card-body">
          {this.props.canManageAdminOrganizationAccess && (
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">Access to organizations</label>
                  {this.props.organizations.map((o) => {
                    return (
                      <div key={o.keyName}>
                        <input
                          type="checkbox"
                          className="mr-2"
                          onChange={(e) => {
                            this.modifyOrgAccess(o.id, e.target.checked);
                          }}
                          checked={this.state.orgAccess[o.id] || false}
                        />{" "}
                        {o.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="form-label">Roles</label>
                {Object.keys(Roles).map((k) => {
                  const roleName = Roles[k];
                  if (
                    [
                      Roles.User,
                      Roles.SignedUp,
                      Roles.Heaps,
                      Roles.SuperAdmin,
                      Roles.All,
                    ].includes(roleName)
                  ) {
                    return null;
                  }

                  if (
                    roleName === Roles.SuperAdmin &&
                    !this.props.isSuperAdmin
                  ) {
                    return null;
                  }

                  return (
                    <div key={k}>
                      <input
                        type="checkbox"
                        className="mr-2"
                        onChange={(e) => {
                          this.modifyRole(Roles[k], e.target.checked);
                        }}
                        checked={this.state.roles[Roles[k]] || false}
                      />{" "}
                      {Roles[k]}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label className="form-label">Advanced roles</label>
                {Object.keys(Roles).map((k) => {
                  const roleName = Roles[k];
                  if (![Roles.SuperAdmin, Roles.All].includes(roleName)) {
                    return null;
                  }

                  if (
                    roleName === Roles.SuperAdmin &&
                    !this.props.isSuperAdmin
                  ) {
                    return null;
                  }

                  const isSuperAdminRole = roleName === Roles.SuperAdmin;

                  return (
                    <div key={k}>
                      <input
                        type="checkbox"
                        className="mr-2"
                        onChange={(e) => {
                          const checked = e.target.checked;

                          let confirmationMessage = "";
                          const roleType = isSuperAdminRole
                            ? "super admin"
                            : "all";
                          const action = checked ? "give" : "remove";
                          confirmationMessage = `You are about to ${action} ${username} ${roleType} access. Are you sure you want to proceed?`;

                          if (!window.confirm(confirmationMessage)) {
                            return;
                          }

                          this.modifyRole(Roles[k], e.target.checked);
                        }}
                        checked={this.state.roles[Roles[k]] || false}
                      />{" "}
                      {Roles[k]}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AdminUserUpsertForm));
